// Top-level router for the curator SPA.
//
// Public:
//   /login → LoginScreen
//
// Gated (wrapped in <AppShell>):
//   /prompts    → AI Prompt Templates (Phase D)
//   /dashboard  → News Story Discovery (Phase E)
//   /article/:id → Article Review (Phase F)
//   /desk       → Editor's Desk chat (Phase G)
//   /broadcast  → Broadcast composer (Phase H)
//   /curations  → Curations placeholder (follow-up plan)
//
// On first load AuthProvider probes /auth/me; while that's in flight we
// show a splash so the user doesn't see a flicker between states. Default
// route /  redirects to /dashboard so editors land on something useful.
import { Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import LoginScreen from "./auth/LoginScreen";
import PromptTemplatesPage from "./pages/PromptTemplates";
import DashboardPage from "./pages/Dashboard";
import ArticleReviewPage from "./pages/ArticleReview/[id]";
import EditorsDeskPage from "./pages/EditorsDesk";
import BroadcastPage from "./pages/Broadcast";
import StudioPage from "./pages/Studio";
import StudioEpisodePage from "./pages/Studio/[id]";
import ContextCardsPage from "./pages/ContextCards";
import ContextCardEditorPage from "./pages/ContextCards/[id]";
import EntityGraphPage from "./pages/EntityGraph";
import CurationsPage from "./pages/Curations";
import AdminPayoutsPage from "./pages/AdminPayouts";
import AdminPayoutDetailPage from "./pages/AdminPayouts/detail";
import AdminCreditsPage from "./pages/AdminCredits";
import AdminTipsPage from "./pages/AdminTips";
import AdminSubscriptionProductsPage from "./pages/AdminSubscriptionProducts";
import AdminSubscriptionsPage from "./pages/AdminSubscriptions";
import AdminSubscriptionPaymentsPage from "./pages/AdminSubscriptionPayments";
import AdminPatronsPage from "./pages/AdminPatrons";
import AdminPatronProgramPage from "./pages/AdminPatronProgram";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Navigate to="/dashboard" replace />
            </RequireAuth>
          }
        />
        <Route
          path="/prompts"
          element={
            <RequireAuth>
              <AppShell>
                <PromptTemplatesPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <AppShell>
                <DashboardPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/article/:id"
          element={
            <RequireAuth>
              <AppShell>
                <ArticleReviewPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/desk"
          element={
            <RequireAuth>
              <AppShell>
                <EditorsDeskPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/broadcast"
          element={
            <RequireAuth>
              <AppShell>
                <BroadcastPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/studio"
          element={
            <RequireAuth>
              <AppShell>
                <StudioPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/studio/:id"
          element={
            <RequireAuth>
              <AppShell>
                <StudioEpisodePage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/context-cards"
          element={
            <RequireAuth>
              <AppShell>
                <ContextCardsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/context-cards/:id"
          element={
            <RequireAuth>
              <AppShell>
                <ContextCardEditorPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/graph"
          element={
            <RequireAuth>
              <AppShell>
                <EntityGraphPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/curations"
          element={
            <RequireAuth>
              <AppShell>
                <CurationsPage />
              </AppShell>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/payouts"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminPayoutsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/payouts/:id"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminPayoutDetailPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/credits"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminCreditsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/tips"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminTipsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/subscription-products"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminSubscriptionProductsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/subscriptions"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminSubscriptionsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/subscription-payments"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminSubscriptionPaymentsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/patrons"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminPatronsPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route
          path="/admin/patrons/program"
          element={
            <RequireAuth>
              <RequireAdmin>
                <AppShell>
                  <AdminPatronProgramPage />
                </AppShell>
              </RequireAdmin>
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AuthProvider>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/dashboard" replace />;
  return <LoginScreen />;
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function Splash() {
  return (
    <div style={splashStyles.outer}>
      <div style={splashStyles.dot} />
    </div>
  );
}

const splashStyles: Record<string, React.CSSProperties> = {
  outer: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 999,
    background: "var(--accent)",
    opacity: 0.6,
  },
};
